import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminGetArticle, adminCreateArticle, adminUpdateArticle } from '../../lib/api';
import { Loader2, Save, ArrowLeft, ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../../styles/rich-editor.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent', 'align',
  'blockquote', 'code-block',
  'link', 'image',
];

const CATEGORIES = ['Environnement', 'Santé', 'Pratique', 'Technique', 'International'];

const EMPTY = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  image: '',
  author: 'Kiosques à Eau',
  category: 'Environnement',
  readTime: '5 min',
  date: '',
  published: true,
};

export default function AdminArticleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isNew) return;
    adminGetArticle(id)
      .then((data) => setForm({ ...EMPTY, ...data }))
      .catch(() => navigate('/admin/articles'))
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await adminCreateArticle(form);
        toast({ title: 'Article créé' });
      } else {
        await adminUpdateArticle(id, form);
        toast({ title: 'Article mis à jour' });
      }
      navigate('/admin/articles');
    } catch (err) {
      toast({ title: 'Erreur', description: 'Sauvegarde impossible.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <Link to="/admin/articles" className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-900 mb-4">
        <ArrowLeft className="w-4 h-4" />Retour aux articles
      </Link>
      <header className="mb-8">
        <h1 className="font-display font-extrabold text-3xl text-sky-950">{isNew ? 'Nouvel article' : 'Modifier l\'article'}</h1>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-2xl border border-sky-100 p-6 space-y-4">
          <div>
            <Label>Titre *</Label>
            <Input required value={form.title} onChange={(e) => update('title', e.target.value)} className="h-11 rounded-xl border-sky-200 mt-1.5 text-base font-semibold" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Slug (URL)</Label>
              <Input value={form.slug} onChange={(e) => update('slug', e.target.value)} placeholder="auto si vide" className="h-11 rounded-xl border-sky-200 mt-1.5 font-mono text-sm" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => update('category', v)}>
                <SelectTrigger className="h-11 rounded-xl border-sky-200 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Image (URL)</Label>
            <div className="relative mt-1.5">
              <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={form.image} onChange={(e) => update('image', e.target.value)} placeholder="https://..." className="h-11 pl-11 rounded-xl border-sky-200 text-sm" />
            </div>
            {form.image && <img src={form.image} alt="" className="mt-3 w-full max-w-md aspect-[16/9] object-cover rounded-xl border border-sky-100" />}
          </div>
          <div>
            <Label>Résumé / Extrait</Label>
            <Textarea rows={3} value={form.excerpt} onChange={(e) => update('excerpt', e.target.value)} className="rounded-xl border-sky-200 mt-1.5 resize-none" />
          </div>
          <div>
            <Label>Contenu *</Label>
            <p className="text-xs text-slate-500 mt-1 mb-2">Utilisez les boutons de la barre d'outils pour mettre en forme (titres, gras, listes, citations, liens, images…).</p>
            <div className="mt-1.5 rounded-xl border border-sky-200 overflow-hidden bg-white" data-testid="article-content-editor">
              <ReactQuill
                theme="snow"
                value={form.content || ''}
                onChange={(html) => update('content', html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Rédigez votre article ici — titres, listes, gras, liens et images sont pris en charge."
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Auteur</Label>
              <Input value={form.author} onChange={(e) => update('author', e.target.value)} className="h-11 rounded-xl border-sky-200 mt-1.5" />
            </div>
            <div>
              <Label>Date affichée</Label>
              <Input value={form.date} onChange={(e) => update('date', e.target.value)} placeholder="ex: 15/03/2025" className="h-11 rounded-xl border-sky-200 mt-1.5" />
            </div>
            <div>
              <Label>Temps de lecture</Label>
              <Input value={form.readTime} onChange={(e) => update('readTime', e.target.value)} placeholder="ex: 5 min" className="h-11 rounded-xl border-sky-200 mt-1.5" />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-sky-50/60 border border-sky-100">
            <div>
              <p className="font-semibold text-sky-950 text-sm">Publié</p>
              <p className="text-xs text-slate-500">L'article apparaît sur le site public.</p>
            </div>
            <Switch checked={form.published} onCheckedChange={(v) => update('published', v)} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} className="h-12 px-6 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-bold rounded-xl border-0">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {isNew ? 'Créer l\'article' : 'Enregistrer'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/articles')} className="h-12 px-6 rounded-xl border-sky-200">Annuler</Button>
        </div>
      </form>
    </div>
  );
}
